import QuestionEditor from '../../QuestionEditor'
import { snapshotable, serializable } from 'legend-builder/data/serialization'
import { cascadeFieldValidation } from 'legend-builder/mixins/validation'
import { observable, computed } from 'mobx'
import RynoEditor from 'legend-builder/ryno-editor/RynoEditor'
import { func, required } from 'validx'

/**
 * A value collected by the personal tax organizer. Later elements reference
 * it by ID, like a Variable. Its value is the organizer's value when present,
 * otherwise the formula result, otherwise the binding type's default.
 */
@snapshotable
export default class PersonalOrganizerCollectedValueQuestionEditor extends QuestionEditor {
  static friendlyType = 'Organizer Collected Value'
  static type = 'PersonalOrganizerCollectedValue'
  static viewOrder = 6
  static isMetaElement = true
  static iconColor = '#0891B2'

  /**
   * Element label.
   */
  @observable
  @serializable
  label = ''

  /**
   * The collected value binding. It decides the value type and which
   * organizer value we get.
   */
  @observable
  @serializable
  collectBindingId = ''

  /**
   * The fallback value formula, used when the organizer has no value.
   */
  @serializable
  expr

  rules = {
    collectBindingId: [
      required('A collected value binding is required.'),
      func(
        () => !this.legendEditor.organizerBindings || !!this.binding,
        'Unknown collected value binding.'
      ),
    ],
    expr: [
      func(
        (ctx) => (ctx.value.value ? ctx.value.analyze() : true),
        'There are formula errors.'
      ),
    ],
  }

  constructor(attrs, opts) {
    super(null, { ...opts, parse: false })
    this.expr = new RynoEditor({
      getSymbolTable: () => this.symbolTable,
      getNeededType: () => this.valueType,
    })
    this.set(attrs, opts)
    cascadeFieldValidation(this, {
      collectBindingId: ['expr'],
    })
  }

  /**
   * All collected value bindings in the catalog.
   */
  @computed
  get catalog() {
    return this.legendEditor.organizerBindings?.personal.collect ?? []
  }

  /**
   * The selected binding, if it is in the catalog.
   */
  @computed
  get binding() {
    return this.catalog.find((b) => b.id === this.collectBindingId) ?? null
  }

  /**
   * Tells the shell what type of output this element has. The binding
   * decides it, so there is no type until a binding is selected.
   */
  @computed
  get valueType() {
    return this.binding?.valueType ?? null
  }

  /**
   * Bindings that can be selected. A binding can only be collected by one
   * element, so bindings used by other elements are left out.
   */
  @computed
  get bindingOptions() {
    const otherElements = this.legendEditor.questionsBuilder.allQuestions.filter(
      (q) => q.type === this.type && q !== this
    )
    const usedBindingIds = otherElements.map((q) => q.collectBindingId)
    return this.catalog
      .filter((b) => !usedBindingIds.includes(b.id))
      .map((b) => [b.id, b.displayName])
  }

  /**
   * The symbol table to use for the Ryno Semantic Analyzer.
   */
  @computed
  get symbolTable() {
    return this.legendEditor.getSymbolTableFor(this)
  }

  parse({ expr, ...attrs }, opts) {
    this.expr.setValue(expr)
    return super.parse(attrs, opts)
  }
}
